import { STAKES, VENUES, type GameId } from '@tira/protocol';
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  useSyncExternalStore,
  type CSSProperties,
  type JSX,
  type ReactNode,
} from 'react';
import { ChipCount, formatChips } from '../../components/ChipCount.js';
import { FullscreenButton } from '../../components/FullscreenButton.js';
import { Icon } from '../../components/Icon.js';
import { PlayerPuck } from '../../components/PlayerPuck.js';
import { TableTalk, TableTalkFloat } from '../../components/TableTalk.js';
import { VoiceTalk } from '../../components/VoiceTalk.js';
import { useT } from '../../i18n/index.js';
import { socket } from '../../net/socket.js';
import { useVoice } from '../../sound/voice.js';
import { FRESH_SEQ, dealPlan, flightPlan, runFlights } from './flights.js';
import { useApp } from '../../state/store.js';
import {
  CAMERA_LIMITS,
  DEFAULT_CAMERA,
  currentPrefs,
  savePrefs,
  subscribePrefs,
  type Camera,
  type TableView,
} from '../lib/prefs.js';
import './room.css';

/**
 * The room every venue plays in.
 *
 * Boards hand it a surface and a hand; it supplies the station, the camera,
 * the furniture and the HUD. The design doc's note on §3a — "the HUD from 2a
 * overlays this" — is taken literally: the bar, pods, pot, toast and actions
 * are the same elements under both cameras, and only the table changes.
 */

/** The box the mocks are drawn at. Everything inside is in these pixels. */
export const SCENE_W = 880;
export const SCENE_H = 495;

export interface GameRoomProps {
  /**
   * Lies flat on the table, positioned in table pixels. Children that should
   * stand up (standees, held cards, wall tiles) carry the `up` class.
   */
  felt: ReactNode;
  /** Your hand or rack, on the near rail in screen space. */
  hand?: ReactNode;
  /** Action pills, bottom right. */
  actions?: ReactNode;
  /** One line of Taglish above the rail — what just happened, or what to do. */
  toast?: ReactNode;
  /** Anything else the board wants in the HUD layer, already positioned. */
  aside?: ReactNode;
}

export function GameRoom({ felt, hand, actions, toast, aside }: GameRoomProps): JSX.Element {
  const table = useApp((s) => s.table);
  const match = useApp((s) => s.match);
  const status = useApp((s) => s.status);
  const me = useApp((s) => s.me);
  const balance = useApp((s) => s.balance);
  const go = useApp((s) => s.go);
  const settling = useApp((s) => s.settling !== null);
  const showReceipt = useApp((s) => s.showReceipt);
  const speaking = useVoice((s) => s.speaking);

  const t = useT();
  const view = useTableView();
  const cam = useCamera();
  const camDrag = useCameraDrag(view === 'room');
  const game: GameId = match?.game ?? table?.game ?? 'dama';
  const venue = VENUES[game];

  const fitRef = useRef<HTMLDivElement>(null);
  const fit = useSceneFit(fitRef);

  /*
   * The flight layer's ear. Subscribed while a room is mounted and not a
   * moment longer — flights need this room's anchors, and a store event with
   * no room on screen has nowhere to fly anything anyway.
   */
  useEffect(() => {
    const pick = (s: ReturnType<typeof useApp.getState>) => ({
      match: s.match,
      receipt: s.receipt,
      me: s.me,
      table: s.table,
      talk: s.talk,
      toast: s.toast,
    });
    let prev = pick(useApp.getState());
    /*
     * The hand that mounted this room dealt before the subscription existed.
     * If it is still young, deal it now; a room joined mid-hand (resync,
     * spectate) has a high seq and gets no phantom riffle.
     */
    if (prev.match && prev.match.seq <= FRESH_SEQ) {
      const root = fitRef.current;
      if (root) runFlights(root, dealPlan(prev));
    }
    return useApp.subscribe((state) => {
      const next = pick(state);
      const root = fitRef.current;
      if (root) runFlights(root, flightPlan(prev, next));
      prev = next;
    });
  }, []);

  const seats = table?.seats ?? [];
  const mySeat = match && match.seat >= 0 ? match.seat : seatOf(seats, me?.userId);
  const seated = mySeat !== null;
  const speakingSeats = new Set(speaking.filter((x) => x.until > Date.now()).map((x) => x.seat));
  const count = Math.max(seats.length, venue.seats[0] ?? 2);
  const pods = podSlots(count);

  const pot = table ? table.buyIn * Math.max(seats.filter((s) => s !== null).length, 1) : 0;
  const tongBps = table ? STAKES[table.tier].tongBps : 500;

  const leave = (): void => {
    if (table) socket.send(seated ? { t: 'table.leave', tableId: table.tableId } : { t: 'spectate.leave', tableId: table.tableId });
    go({ s: 'compound' });
  };

  /** Seat i's slot, rotated so the local player is always the near one. */
  const slotFor = (i: number): Slot | null => pods[rotateSeat(i, mySeat, count)] ?? null;

  return (
    <div className="room" ref={fitRef}>
      <div
        className="room__scene"
        data-venue={game}
        data-view={view}
        style={
          {
            '--fit': fit,
            '--tilt': cam.tilt,
            '--pan': cam.pan,
            '--zoom': cam.zoom,
          } as CSSProperties
        }
      >
        <div className="room__back" />
        <div className="room__lamp" />

        {view === 'room' ? (
          <div
            className="room__grab"
            aria-hidden="true"
            onPointerDown={camDrag.onPointerDown}
            onWheel={(e) => nudgeCamera({ zoom: e.deltaY * -0.0012 })}
          />
        ) : null}

        <div className="room__world">
          <div className="room__stage">
            <div className="room__floor" />
            <div className="room__floor room__floor--fade" />
            <div className="room__mat" />
            <div className="room__pool" />

            {seats.map((seat, i) => {
              const slot = slotFor(i);
              // You are behind the camera; your own standee would only ever be
              // the back of a head in the way of your own hand.
              if (!seat || slot === null || i === mySeat || slot.near) return null;
              return (
                <div
                  key={`sit-${seat.userId}`}
                  className="room__sit"
                  data-turn={match?.turn === i}
                  style={{ '--x': `${slot.sx}px`, '--y': `${slot.sy}px` } as CSSProperties}
                />
              );
            })}

            <div className="room__table">
              <span className="room__side room__side--n" />
              <span className="room__side room__side--e" />
              <span className="room__side room__side--s" />
              <span className="room__side room__side--w" />
              <div className="room__felt">{felt}</div>
            </div>
          </div>
        </div>

        <div className="room__cone" />
        <div className="room__vig" />

        <div className="room__hud" data-rail={hand !== undefined}>
          <div className="room__bar">
            <button className="room__back-btn" type="button" onClick={leave}>
              <span aria-hidden="true">‹</span>
              {seated ? t('room.leave') : t('table.stop')}
            </button>
            <b>{t(`venue.${game}`)}</b>
            <span>
              {t(`venue.${game}.station`)}
              {table ? ` · ${t(`stake.${table.tier}`)}` : ''}
              {table?.kuboCode ? ` · ${t('table.kubo', { code: table.kuboCode })}` : ''}
            </span>
            <span className="room__spacer" />
            <ViewSwitch view={view} />
            <FullscreenButton className="room__iconbtn" size={16} />
            <ChipCount value={balance} size="sm" />
            <span className="room__conn" data-state={status}>
              <Icon name="signal" size={12} />
              {t(`conn.state.${status}`)}
            </span>
          </div>

          {/*
            The pot rides the table's far edge as a pill, not the full receipt:
            at 880×495 the receipt covered the top three ranks of every board.
            The tong stays on it, worded by the same function the receipt uses,
            so the house cut is still readable at all times (PRD §7.3).
          */}
          <div className="room__pot">
            <span className="room__pot-pill">
              {t('table.pot')} <b>{formatChips(pot)}</b> · {t('room.tong', { pct: tongBps / 100 })}
            </span>
          </div>

          {seats.map((seat, i) => {
            const slot = slotFor(i);
            if (!seat || slot === null) return null;
            return (
              <div
                key={seat.userId}
                className="room__pod"
                data-fly={`seat-${i}`}
                style={{ '--x': `${slot.px}px`, '--y': `${slot.py}px` } as CSSProperties}
              >
                <PlayerPuck
                  puck={seat}
                  turn={match?.turn === i}
                  deadline={match?.turn === i ? match.deadline : null}
                  turnMs={match?.turnMs ?? venue.turnMs}
                  you={i === mySeat}
                  venue={game}
                />
                {speakingSeats.has(i) ? (
                  <span className="room__speaking" aria-hidden="true">
                    <Icon name="mic" size={10} />
                  </span>
                ) : null}
              </div>
            );
          })}

          <div className="room__social">
            <span className="room__miron">
              <Icon name="miron" size={12} />
              {t('room.miron', { n: table?.spectators ?? 0 })}
            </span>
            {table && seated && !(me?.limits.muteTableTalk ?? false) ? (
              <VoiceTalk tableId={table.tableId} disabled={status !== 'open'} />
            ) : null}
            {table ? (
              <TableTalk
                onSend={(preset) => socket.send({ t: 'table.talk', tableId: table.tableId, preset })}
                muted={me?.limits.muteTableTalk ?? false}
                disabled={status !== 'open'}
              />
            ) : null}
          </div>

          {me?.limits.muteTableTalk ? null : <TableTalkFloat />}

          {/*
            A live region, because the toast is where every venue says the thing
            with a clock on it — "sagutin mo", "may bumagsak", a watawat one turn
            from home. Announcing it belongs here rather than in six boards, and
            Gera lost its announcement precisely by moving its line up into this
            slot from a `role="status"` of its own.
          */}
          {toast ? (
            <div className="room__toast" role="status">
              {toast}
            </div>
          ) : null}

          {/*
            The hand is over and the chips have moved; the board is holding the
            screen so its ending can be read. Every venue draws its own over-state
            underneath this — all it adds is the way on, and the fact that waiting
            is optional.
          */}
          {settling ? (
            <button className="room__settled" type="button" onClick={showReceipt}>
              <b>{t('room.settled.title')}</b>
              <span>{t('room.settled.sub')}</span>
            </button>
          ) : null}
          {hand ? <div className="room__rail">{hand}</div> : null}
          {actions ? <div className="room__acts">{actions}</div> : null}
          {aside}
        </div>
      </div>
    </div>
  );
}

/* ── Camera ──────────────────────────────────────────────────────────────── */

/** The device's chosen camera, re-rendering every table when it changes. */
export function useTableView(): TableView {
  return useSyncExternalStore(
    subscribePrefs,
    () => currentPrefs().tableView,
    () => 'room' as const,
  );
}

export function setTableView(v: TableView): void {
  savePrefs({ ...currentPrefs(), tableView: v });
}

/** The seated camera this device is using. */
export function useCamera(): Camera {
  return useSyncExternalStore(
    subscribePrefs,
    () => currentPrefs().camera,
    () => DEFAULT_CAMERA,
  );
}

const clamp = (k: keyof Camera, v: number): number => {
  const { min, max } = CAMERA_LIMITS[k];
  return Math.min(Math.max(v, min), max);
};

export function nudgeCamera(by: Partial<Camera>): void {
  const prefs = currentPrefs();
  const cam = prefs.camera;
  savePrefs({
    ...prefs,
    camera: {
      tilt: clamp('tilt', cam.tilt + (by.tilt ?? 0)),
      pan: clamp('pan', cam.pan + (by.pan ?? 0)),
      zoom: clamp('zoom', cam.zoom + (by.zoom ?? 0)),
    },
  });
}

export function setCamera(cam: Camera): void {
  savePrefs({
    ...currentPrefs(),
    camera: { tilt: clamp('tilt', cam.tilt), pan: clamp('pan', cam.pan), zoom: clamp('zoom', cam.zoom) },
  });
}

/**
 * Drag the table to move your chair.
 *
 * A camera wants to be grabbed, not typed into, so this listens on the felt's
 * own layer rather than putting three sliders on a phone screen. It only ever
 * claims a gesture that starts on empty felt or the surround — a pointerdown on
 * a tile, a card or a square is that piece's, and this never sees it, because
 * the boards' own controls sit above this layer and stop it there.
 *
 * Horizontal is pan and vertical is tilt, which is the way round a hand
 * expects: push the table away and you rise over it. Two fingers pinch to lean
 * in, and the second finger ends the swing — a pinch that also spun the table
 * would be nobody's idea of zooming.
 *
 * The pinch is a ratio against the span the fingers started at, not a running
 * delta, so a gesture cannot drift and letting go halfway leaves the chair
 * exactly where the fingers put it. Lifting one of two hands the gesture back
 * to the other rather than ending it.
 */
function useCameraDrag(enabled: boolean): {
  onPointerDown: (e: React.PointerEvent) => void;
} {
  /** Every finger currently down on the grab surface, newest last. */
  const live = useRef(new Map<number, { x: number; y: number }>());
  /** One finger: swing the chair. Anchored so a gesture never drifts. */
  const orbit = useRef<{ id: number; x: number; y: number; cam: Camera } | null>(null);
  /** Two fingers: lean. Ratio against the span they started at. */
  const pinch = useRef<{ span: number; zoom: number } | null>(null);

  const span = (): number => {
    const [a, b] = [...live.current.values()];
    if (!a || !b) return 0;
    return Math.hypot(a.x - b.x, a.y - b.y);
  };

  /** Re-anchor the orbit to a finger that is still down, so nothing jumps. */
  const anchor = (id: number): void => {
    const p = live.current.get(id);
    if (p) orbit.current = { id, x: p.x, y: p.y, cam: currentPrefs().camera };
  };

  const onPointerDown = useCallback(
    (e: React.PointerEvent) => {
      if (!enabled || e.button !== 0) return;
      live.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
      // Capture keeps a finger that slides off the surface still ours. It is a
      // nicety, not the mechanism — the listeners are on the window — so a
      // browser that refuses must not take the rest of the gesture down with
      // it, which is exactly what an unguarded throw here did.
      try {
        (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
      } catch {
        /* no capture; the window listeners still see the gesture */
      }
      if (live.current.size === 1) {
        anchor(e.pointerId);
        pinch.current = null;
      } else if (live.current.size === 2) {
        // The second finger starts a lean and ends the swing, or the table
        // would spin under a pinch that is only ever meant to zoom.
        orbit.current = null;
        pinch.current = { span: span(), zoom: currentPrefs().camera.zoom };
      }
    },
    [enabled],
  );

  useEffect(() => {
    if (!enabled) return;

    const move = (e: PointerEvent): void => {
      const p = live.current.get(e.pointerId);
      if (!p) return;
      p.x = e.clientX;
      p.y = e.clientY;

      const two = pinch.current;
      if (two !== null && live.current.size >= 2) {
        const now = span();
        if (two.span > 8 && now > 8) {
          const cam = currentPrefs().camera;
          setCamera({ tilt: cam.tilt, pan: cam.pan, zoom: two.zoom * (now / two.span) });
        }
        return;
      }

      const d = orbit.current;
      if (!d || e.pointerId !== d.id) return;
      // Degrees per CSS pixel, scaled so a full swipe crosses the range once.
      setCamera({
        tilt: d.cam.tilt - (e.clientY - d.y) * 0.16,
        pan: d.cam.pan + (e.clientX - d.x) * 0.14,
        zoom: d.cam.zoom,
      });
    };

    const end = (e: PointerEvent): void => {
      if (!live.current.delete(e.pointerId)) return;
      pinch.current = null;
      if (orbit.current?.id === e.pointerId) orbit.current = null;
      // Lifting one of two fingers hands the gesture back to the other rather
      // than ending it, which is what a hand doing this expects.
      const [rest] = [...live.current.keys()];
      if (rest !== undefined) anchor(rest);
      else orbit.current = null;
    };

    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', end);
    window.addEventListener('pointercancel', end);
    return () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', end);
      window.removeEventListener('pointercancel', end);
    };
  }, [enabled]);

  return { onPointerDown };
}

function ViewSwitch({ view }: { view: TableView }): JSX.Element {
  const t = useT();
  const cam = useCamera();
  const moved =
    Math.abs(cam.tilt - DEFAULT_CAMERA.tilt) > 0.5 ||
    Math.abs(cam.pan - DEFAULT_CAMERA.pan) > 0.5 ||
    Math.abs(cam.zoom - DEFAULT_CAMERA.zoom) > 0.02;
  return (
    <span className="room__view" role="group" aria-label={t('room.view.label')}>
      <button type="button" aria-pressed={view === 'room'} onClick={() => setTableView('room')}>
        {t('room.view.seated')}
      </button>
      <button type="button" aria-pressed={view === 'top'} onClick={() => setTableView('top')}>
        {t('room.view.top')}
      </button>
      {view === 'room' && moved ? (
        <button
          type="button"
          className="room__view-reset"
          title={t('room.view.reset')}
          onClick={() => setCamera(DEFAULT_CAMERA)}
        >
          {t('room.view.reset.short')}
        </button>
      ) : null}
    </span>
  );
}

/**
 * One uniform scale from the 880×495 design box onto the viewport. Scaling —
 * rather than reflowing — is what keeps a 3D stage honest: perspective,
 * translateZ and the counter-rotation are all in the same pixel space, so a
 * table that reads right in the mock reads right on a 5" phone.
 */
export function useSceneFit(ref: React.RefObject<HTMLElement | null>): number {
  const [fit, setFit] = useState(1);

  const measure = useCallback((el: HTMLElement) => {
    const { width, height } = el.getBoundingClientRect();
    if (width > 0 && height > 0) setFit(Math.min(width / SCENE_W, height / SCENE_H));
  }, []);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    measure(el);
  }, [ref, measure]);

  useEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => measure(el));
    ro.observe(el);
    return () => ro.disconnect();
  }, [ref, measure]);

  return fit;
}

/* ── Seat geometry ───────────────────────────────────────────────────────── */

export interface Slot {
  /** Pod position, in scene pixels. */
  px: number;
  py: number;
  /** Where that player stands, in table-local pixels. */
  sx: number;
  sy: number;
  /** The local player's chair — no standee, and the hand rail belongs to it. */
  near?: boolean;
}

/**
 * Where each chair sits, indexed by seat count. Slot 0 is always the local
 * player's, and the rest run clockwise from there; GameRoom rotates the real
 * seat indices into this frame so you are always the one at the bottom.
 *
 * Pod coordinates come from the design doc's own mocks (podsM/podsT/podsD in
 * the bundled document) so the HUD lands where it was drawn.
 */
const POD_SLOTS: Record<number, Slot[]> = {
  2: [
    { px: 16, py: 396, sx: 0, sy: 245, near: true },
    { px: 16, py: 62, sx: 0, sy: -245 },
  ],
  3: [
    { px: 8, py: 418, sx: 0, sy: 245, near: true },
    { px: 128, py: 54, sx: -245, sy: -95 },
    { px: 600, py: 54, sx: 245, sy: -95 },
  ],
  4: [
    { px: 8, py: 412, sx: 0, sy: 262, near: true },
    { px: 10, py: 196, sx: -262, sy: 0 },
    { px: 118, py: 48, sx: 0, sy: -262 },
    { px: 700, py: 196, sx: 262, sy: 0 },
  ],
};

/**
 * Chairs for a table of `count`, falling back to the four-seat ring for any
 * size the design doc does not draw. Pusoy seats two, three or four, so a
 * missing entry is a real possibility rather than a defensive branch.
 */
export function podSlots(count: number): Slot[] {
  return POD_SLOTS[count] ?? POD_SLOTS[4] ?? [];
}

/**
 * Which chair seat `i` occupies once the table is turned so the local player
 * is the one at the bottom. A miron has no seat and gets the table unturned.
 */
export function rotateSeat(i: number, mySeat: number | null, count: number): number {
  return (i - (mySeat ?? 0) + count) % count;
}

function seatOf(
  seats: readonly ({ userId: string } | null)[],
  userId: string | undefined,
): number | null {
  if (!userId) return null;
  const i = seats.findIndex((s) => s?.userId === userId);
  return i < 0 ? null : i;
}
